// ─────────────────────────────────────────────────────────────────────────────
// Texto com imagem embutida — o parser do marcador.
//
// A imagem deixa de ser uma lista no fim do formulário e passa a ser um
// caractere no meio da frase: quem escreve decide onde ela entra, e a ordem do
// raciocínio se mantém — "o erro aparece assim: [[anexo:0f3c…]] e depois de
// reprocessar volta ao normal".
//
// Formato do marcador: [[anexo:<id>]]
//
// Por que marcador e não markdown `![](data:…)`:
//   · quem escreve é analista de suporte, não escreve markdown, e um `!` solto
//     não pode virar imagem quebrada;
//   · a imagem no Orion é base64 e chega a ter megabytes — colar isso dentro do
//     texto que vai para a IA e para o histórico é inviável. O marcador guarda
//     só o id, e o conteúdo fica no array de imagens;
//   · [[…]] não aparece em texto técnico digitado à mão, então não há escape.
//
// Este arquivo é PURO: não conhece React, Electron nem webhook. É ele que
// decide a ordem em que a imagem aparece no PDF.
//
// Adaptado do textoComAnexos.js do Nexus. A diferença é que lá o anexo vive num
// bucket e tem uuid do banco; aqui ele vive em memória, com um id gerado no
// próprio cliente — por isso o padrão aceita id curto.
// ─────────────────────────────────────────────────────────────────────────────

const PADRAO = /\[\[anexo:([0-9a-zA-Z-]{6,40})\]\]/g;

export const marcadorDe = (id) => `[[anexo:${id}]]`;

/**
 * Id novo para uma imagem anexada.
 * randomUUID exige contexto seguro (https/localhost); o Math.random cobre o
 * resto — o id só precisa ser único dentro desta formalização.
 */
export function novoIdAnexo() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `img-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Ids na ORDEM em que aparecem no texto, sem repetir.
 * A ordem importa: é ela que o documento usa para desenhar as imagens na
 * sequência escrita.
 */
export function idsNoTexto(texto) {
  const achados = [];
  String(texto || '').replace(PADRAO, (_, id) => {
    if (!achados.includes(id)) achados.push(id);
    return '';
  });
  return achados;
}

export function temAnexo(texto) {
  return idsNoTexto(texto).length > 0;
}

/**
 * Texto sem os marcadores — é isto que vai para a IA e para o histórico.
 * A IA não entende `[[anexo:…]]` e devolveria o marcador picado no meio de uma
 * frase; melhor ela nunca ver.
 */
export function semMarcadores(texto) {
  return String(texto || '')
    .replace(PADRAO, '')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Quebra o texto em pedaços na ordem escrita:
 * [{ tipo: 'texto', valor }, { tipo: 'anexo', id }, …].
 *
 * `conhecidos` (opcional) = ids que existem de verdade. Marcador órfão — a
 * imagem foi removida, o texto veio de um histórico antigo — vira nada, e não
 * um retângulo quebrado no meio do documento.
 */
export function partesDoTexto(texto, conhecidos = null) {
  const bruto = String(texto || '');
  const partes = [];
  let ultimo = 0;

  bruto.replace(PADRAO, (marca, id, indice) => {
    const antes = bruto.slice(ultimo, indice);
    if (antes) partes.push({ tipo: 'texto', valor: antes });
    if (!conhecidos || conhecidos.includes(id)) partes.push({ tipo: 'anexo', id });
    ultimo = indice + marca.length;
    return marca;
  });

  const resto = bruto.slice(ultimo);
  if (resto) partes.push({ tipo: 'texto', valor: resto });
  return partes;
}

/**
 * Insere o marcador na posição do cursor, garantindo linha própria.
 *
 * Um marcador colado no fim da frase ("...na emissão:[[anexo:…]]") funciona,
 * mas a imagem desenhada logo abaixo de um texto sem respiro fica ilegível no
 * PDF — e é o MESMO texto que vai para a tela e para o documento.
 */
export function inserirMarcador(texto, id, posicao) {
  const t = String(texto || '');
  const p = Number.isInteger(posicao) && posicao >= 0 && posicao <= t.length ? posicao : t.length;
  const antes = t.slice(0, p);
  const depois = t.slice(p);

  const quebraAntes = antes.length > 0 && !antes.endsWith('\n');
  const quebraDepois = depois.length > 0 && !depois.startsWith('\n');

  const bloco = `${quebraAntes ? '\n' : ''}${marcadorDe(id)}${quebraDepois ? '\n' : ''}`;
  return {
    texto: `${antes}${bloco}${depois}`,
    // Onde deixar o cursor: logo depois do que foi inserido, para continuar
    // escrevendo sem ter de clicar de novo no campo.
    cursor: antes.length + bloco.length,
  };
}

export function removerMarcador(texto, id) {
  const alvo = new RegExp(`\\n?\\[\\[anexo:${id}\\]\\]\\n?`, 'gi');
  return String(texto || '').replace(alvo, '\n').replace(/\n{3,}/g, '\n\n');
}

/**
 * As imagens na ordem em que aparecem no texto.
 *
 * O que não foi citado (anexado e depois apagado do texto, ou vindo de um
 * rascunho antigo) vai no fim, na ordem em que foi anexado — some da posição,
 * mas não some do envio.
 */
export function ordenarPeloTexto(texto, imagens = []) {
  const ordem = idsNoTexto(texto);
  const porId = new Map(imagens.filter(i => i && i.id).map(i => [i.id, i]));

  const citadas = ordem.map(id => porId.get(id)).filter(Boolean);
  const citadasIds = new Set(citadas.map(i => i.id));
  const soltas = imagens.filter(i => i && !citadasIds.has(i.id));

  return [...citadas, ...soltas];
}

// ── reancoragem ──────────────────────────────────────────────────────────────
//
// A formalização é UMA chamada ao n8n: manda-se o texto e recebe-se outro,
// reescrito. Os marcadores não sobrevivem a isso (nem devem: a IA não os
// entende, ver `semMarcadores`).
//
// Reancorar é recolocá-los no texto que voltou. Não dá para acertar o ponto
// exato — o texto foi reescrito —, mas dá para preservar o que importa: a
// ORDEM, e a proporção de onde cada imagem estava. Uma imagem que ilustrava o
// começo do relato continua no começo; a do fim, no fim.
//
// O operador vê o resultado na fase 3 e pode mover o que quiser antes de gerar
// o documento — por isso "aproximado" é suficiente aqui, e chutar é melhor que
// empilhar tudo no fim.

/** Parágrafos não vazios de um texto, com o índice de onde cada um termina. */
function paragrafosDe(texto) {
  const linhas = String(texto || '').split('\n');
  const lista = [];
  let posicao = 0;
  linhas.forEach((linha) => {
    posicao += linha.length + 1;          // +1 pelo \n consumido
    if (linha.trim()) lista.push({ fim: Math.min(posicao, String(texto).length) });
  });
  return lista;
}

/**
 * Reinsere no `textoNovo` os marcadores que existiam no `textoOriginal`,
 * mantendo a ordem e a posição relativa.
 *
 * Devolve o texto novo com os marcadores. Se o texto novo estiver vazio, o
 * original é devolvido intacto — perder o que a pessoa escreveu é pior que
 * ficar sem a formalização.
 */
export function reancorarMarcadores(textoOriginal, textoNovo) {
  const ids = idsNoTexto(textoOriginal);
  if (!ids.length) return String(textoNovo || '');
  if (!String(textoNovo || '').trim()) return String(textoOriginal || '');

  // Onde cada marcador estava, como fração do texto original sem marcadores.
  const limpoOriginal = semMarcadores(textoOriginal);
  const totalOriginal = Math.max(1, limpoOriginal.length);

  const fracoes = ids.map((id) => {
    const antes = String(textoOriginal).split(marcadorDe(id))[0] || '';
    return { id, fracao: Math.min(1, semMarcadores(antes).length / totalOriginal) };
  });

  const paragrafos = paragrafosDe(textoNovo);
  // Sem parágrafo nenhum (texto de uma linha só): tudo no fim, em ordem.
  if (!paragrafos.length) {
    return `${textoNovo}\n${fracoes.map(f => marcadorDe(f.id)).join('\n')}`;
  }

  // Cada marcador cai no fim do parágrafo correspondente à sua fração. Vários
  // marcadores no mesmo parágrafo ficam juntos, na ordem original.
  const porParagrafo = new Map();
  fracoes.forEach(({ id, fracao }) => {
    const indice = Math.min(
      paragrafos.length - 1,
      Math.floor(fracao * paragrafos.length),
    );
    if (!porParagrafo.has(indice)) porParagrafo.set(indice, []);
    porParagrafo.get(indice).push(id);
  });

  // Monta de trás para frente: inserir do fim para o começo mantém válidos os
  // índices ainda não usados.
  let saida = String(textoNovo);
  [...porParagrafo.keys()].sort((a, b) => b - a).forEach((indice) => {
    const corte = paragrafos[indice].fim;
    const marcas = porParagrafo.get(indice).map(marcadorDe).join('\n');
    saida = `${saida.slice(0, corte)}${marcas}\n${saida.slice(corte)}`;
  });

  return saida.replace(/\n{3,}/g, '\n\n');
}

// ── para o gerador de documento ──────────────────────────────────────────────

/**
 * Converte o texto em blocos do documento, intercalando parágrafos e imagens na
 * ordem escrita. `imagensPorId` é { id: { dataUrl, base64, legenda, nome } }.
 *
 * Continua puro: quem sabe desenhar é o gerador de PDF.
 */
export function blocosComAnexos(texto, imagensPorId = {}) {
  const partes = partesDoTexto(texto, Object.keys(imagensPorId));
  const blocos = [];

  // Junta os pedaços de texto vizinhos num bloco só de parágrafos.
  let acumulado = '';
  const despejar = () => {
    const paragrafos = acumulado
      .split(/\r?\n/)
      .map(p => p.trim())
      .filter(Boolean);
    if (paragrafos.length) blocos.push({ tipo: 'texto', paragrafos });
    acumulado = '';
  };

  partes.forEach((parte) => {
    if (parte.tipo === 'texto') { acumulado += parte.valor; return; }
    const img = imagensPorId[parte.id];
    if (!img) return;
    despejar();
    blocos.push({
      tipo: 'imagem',
      base64: img.base64,
      dataUrl: img.dataUrl,
      legenda: img.legenda || '',
      nome: img.nome || '',
    });
  });
  despejar();

  return blocos;
}
